import {
  FC,
  useState,
  useLayoutEffect,
  useRef,
  useEffect,
  RefObject,
} from "react";
import { roundTo } from "../../utils";

const MIN_INPUT_WIDTH = 24;
const MAX_INPUT_WIDTH = 320;

type LoanAmountInputProps = {
  value: number;
  onValueChange: (value: number) => void;
  inputRef: RefObject<HTMLInputElement>;
  currencySymbol?: string;
};

export const LoanAmountInput: FC<LoanAmountInputProps> = ({
  value,
  onValueChange,
  inputRef,
  currencySymbol = "D",
}) => {
  const loanAmountDisplay = roundTo(value || 0, 2).toLocaleString();
  const measureRef = useRef<HTMLSpanElement>(null);
  const [inputWidth, setInputWidth] = useState(MIN_INPUT_WIDTH);
  const refocusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (el) {
      setInputWidth(
        Math.min(MAX_INPUT_WIDTH, Math.max(MIN_INPUT_WIDTH, el.offsetWidth))
      );
    }
  }, [loanAmountDisplay]);

  const refocusInput = () => {
    const el = inputRef.current;
    if (el) {
      el.focus({ preventScroll: true });
      const len = el.value.length;
      el.setSelectionRange(len, len);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/,/g, "");
    const numericValue = parseFloat(rawValue);
    if (!isNaN(numericValue)) {
      onValueChange(numericValue);
    } else if (rawValue === "") {
      onValueChange(0);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (refocusTimeoutRef.current) clearTimeout(refocusTimeoutRef.current);
    if (e.relatedTarget == null) {
      refocusTimeoutRef.current = setTimeout(refocusInput, 0);
    } else {
      refocusTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (refocusTimeoutRef.current) clearTimeout(refocusTimeoutRef.current);
    };
  }, []);

  return (
    <div className="cx-w-full cx-flex cx-justify-center">
      <div className="cx-text-3xl cx-font-bold cx-flex cx-items-center cx-gap-0 cx-tracking-tight">
        <span
          ref={measureRef}
          aria-hidden
          className="cx-text-3xl cx-font-bold cx-invisible cx-absolute cx-left-[-9999px] cx-pointer-events-none cx-whitespace-nowrap"
        >
          {loanAmountDisplay}
        </span>
        <span className="cx-font-aed-font cx-shrink-0">{currencySymbol}</span>
        <input
          ref={inputRef}
          type="text"
          value={loanAmountDisplay}
          onChange={handleInputChange}
          onBlur={handleBlur}
          autoFocus
          className="cx-bg-transparent cx-border-none cx-outline-none cx-text-3xl cx-font-bold cx-text-left cx-overflow-hidden cx-p-0 cx-m-0 cx-min-w-[24px]"
          style={{ width: inputWidth }}
        />
      </div>
    </div>
  );
};
